// Compensation-plan settings hooks. The `programDefault` / `forGenerator` reads
// 404 when no version has been saved for that scope yet (built-in defaults
// apply) — the queryFn maps that to null so the editor can seed from the always-
// resolving `effective` config instead. A save invalidates the whole prefix.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  compensationPlanApi,
  type LeadGenPlanDto,
  type SaveLeadGenPlanRequest,
} from "@/api/leadGen/compensationPlan";
import { compensationPlanKeys } from "@/hooks/leadGen/compensationPlanKeys";
import { leadGeneratorKeys } from "@/hooks/leadGen/leadGeneratorKeys";

async function nullOn404<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export function useEffectiveConfig(leadGeneratorId: string) {
  return useQuery({
    queryKey: compensationPlanKeys.effective(leadGeneratorId),
    queryFn: () => compensationPlanApi.effective(leadGeneratorId),
  });
}

export function useProgramDefaultPlan() {
  return useQuery<LeadGenPlanDto | null>({
    queryKey: compensationPlanKeys.programDefault(),
    queryFn: () => nullOn404(() => compensationPlanApi.programDefault()),
  });
}

export function useGeneratorPlan(leadGeneratorId: string | null) {
  return useQuery<LeadGenPlanDto | null>({
    queryKey: compensationPlanKeys.forGenerator(leadGeneratorId ?? ""),
    queryFn: () => nullOn404(() => compensationPlanApi.forGenerator(leadGeneratorId as string)),
    enabled: !!leadGeneratorId,
  });
}

export function useSavePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveLeadGenPlanRequest) => compensationPlanApi.save(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: compensationPlanKeys.all });
    },
  });
}

/** Revert a generator to the program default (removes its open override). The
 *  effective config re-resolves to the default AND the roster's `hasCustomPlan`
 *  indicator flips, so refresh BOTH prefixes. */
export function useRevertPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (generatorId: string) => compensationPlanApi.revert(generatorId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: compensationPlanKeys.all });
      void qc.invalidateQueries({ queryKey: leadGeneratorKeys.all });
    },
  });
}
